import { fetchApi } from './client';
import type { PaginatedResponse, VendorCpeMapping } from './types';

interface ListCpeMappingsParams {
    // Numeric catalog-product-candidate id — the canonical vendor key (F3). Use
    // for the detail view: it resolves candidate-only rows whose compound id
    // degrades to `::<slug>`. Backend prefers it over vendor_directory_id.
    catalog_product_candidate_id?: number;
    vendor_directory_id?: string;
    cpe_vendor?: string;
    limit?: number;
    offset?: number;
}

/**
 * List CPE mappings with optional filtering.
 */
export async function listCpeMappings(
    params: ListCpeMappingsParams = {},
): Promise<PaginatedResponse<VendorCpeMapping>> {
    const searchParams = new URLSearchParams();

    if (params.catalog_product_candidate_id !== undefined) {
        searchParams.set(
            'catalog_product_candidate_id',
            String(params.catalog_product_candidate_id),
        );
    }

    if (params.vendor_directory_id) {
        searchParams.set('vendor_directory_id', params.vendor_directory_id);
    }

    if (params.cpe_vendor) {
        searchParams.set('cpe_vendor', params.cpe_vendor);
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }

    if (params.offset !== undefined) {
        searchParams.set('offset', String(params.offset));
    }

    const queryString = searchParams.toString();
    const path = `/api/cpe-mappings${queryString ? `?${queryString}` : ''}`;

    return fetchApi<PaginatedResponse<VendorCpeMapping>>(path);
}

interface CreateCpeMappingParams {
    catalog_product_candidate_id: number;
    cpe_vendor: string;
    cpe_product?: string;
    match_type?: string;
    confidence?: string;
    source?: string;
}

/**
 * Create a new CPE mapping.
 */
export async function createCpeMapping(data: CreateCpeMappingParams): Promise<VendorCpeMapping> {
    return fetchApi<VendorCpeMapping>('/api/cpe-mappings', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

interface UpdateCpeMappingParams {
    cpe_vendor?: string;
    cpe_product?: string;
    match_type?: string;
    confidence?: string;
}

/**
 * Update an existing CPE mapping.
 */
export async function updateCpeMapping(
    id: number,
    data: UpdateCpeMappingParams,
): Promise<VendorCpeMapping> {
    return fetchApi<VendorCpeMapping>(`/api/cpe-mappings/${String(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

/**
 * Delete a CPE mapping.
 */
export async function deleteCpeMapping(id: number): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(`/api/cpe-mappings/${String(id)}`, {
        method: 'DELETE',
    });
}
